import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  DirectionsRenderer,
  Marker,
  OverlayView,
} from "@react-google-maps/api";
import steps_icon from "../assets/icons/steps_icon.png";
import start from "../assets/icons/start.png";
import end from "../assets/icons/end.png";
import moment from "moment-timezone";
import pusher from "../services/pusher";

const containerStyle = {
  width: "100%",
  height: "100%",
};

function Maps(props) {
  const data = props.data;

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyCfkkFnJ7ixENdtACx3-Q5Mewh3wftkCo8",
  });

  const [map, setMap] = useState(null);
  const [directions, setDirections] = useState(null);
  const [showEnd, setShowEnd] = useState(true);
  const [routePath, setRoutePath] = useState([]);
  const [waypoints, setWaypoints] = useState([]);
  const [pickup_lat, set_pickup_lat] = useState(null);
  const [pickup_lon, set_pickup_lon] = useState(null);
  const [dropoff_lat, set_dropoff_lat] = useState(null);
  const [dropoff_lon, set_dropoff_lon] = useState(null);
  const [stepsData, setStepData] = useState([]);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });

  const [driverLocation, setDriverLocation] = useState(null);
  const [driverDirections, setDriverDirections] = useState(null);

  // 🔥 Track previous driver location for route re-calculation
  const [prevDriverLocation, setPrevDriverLocation] = useState(null);
  const [routeRecalculationCount, setRouteRecalculationCount] = useState(0);
  const [travelTime, setTravelTime] = useState(null);
  const [travelDistance, setTravelDistance] = useState(null);

  const directionsServiceRef = useRef(null);
  const mapRef = useRef(null);
  const routeFetchTimeout = useRef(null);

  // 🔥 Calculate distance between two points (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  };

  // 🔥 Check if driver is still on the route path
  const isDriverOnRoute = (driverLat, driverLng, routePoints) => {
    if (!routePoints || routePoints.length === 0) return true;

    // Check distance from driver to each route point
    let minDistance = Infinity;
    for (let point of routePoints) {
      const dist = calculateDistance(
        driverLat,
        driverLng,
        point.lat,
        point.lng,
      );
      if (dist < minDistance) {
        minDistance = dist;
      }
    }

    // If min distance > 0.1 km (100 meters), driver is off route
    return minDistance < 0.1;
  };

  const getStepsStorageKey = () => {
    return data?.id ? `driver_steps_${data.id}` : null;
  };

  const saveStepsToStorage = (steps) => {
    const key = getStepsStorageKey();

    if (!key || !Array.isArray(steps)) return;

    localStorage.setItem(key, JSON.stringify(steps));

    // Same browser/tab ke components ko notify karne ke liye
    window.dispatchEvent(
      new CustomEvent("driver-steps-updated", {
        detail: {
          driverId: data.id,
          steps,
        },
      }),
    );

    console.log("💾 Steps saved to localStorage:", key, steps);
  };

  // 🔥 Get the nearest point on route
  const getNearestRoutePoint = (driverLat, driverLng, routePoints) => {
    if (!routePoints || routePoints.length === 0) return null;

    let nearestPoint = null;
    let minDistance = Infinity;

    for (let point of routePoints) {
      const dist = calculateDistance(
        driverLat,
        driverLng,
        point.lat,
        point.lng,
      );
      if (dist < minDistance) {
        minDistance = dist;
        nearestPoint = point;
      }
    }

    return nearestPoint;
  };

  // Handle driver location updates
  const handleDriverLocationUpdate = (eventData) => {
    let lat = null;
    let lng = null;

    // =========================
    // NORMALIZE PUSHER PAYLOAD
    // =========================
    let payload = eventData;

    // Pusher data agar { data: "JSON_STRING" } format mein aaye
    if (typeof eventData?.data === "string") {
      try {
        payload = JSON.parse(eventData.data);
      } catch (error) {
        console.error("❌ Failed to parse Pusher data:", error);
        return;
      }
    } else if (eventData?.data && typeof eventData.data === "object") {
      payload = eventData.data;
    }

    console.log("🔥 Pusher parsed payload:", payload);

    // =========================
    // SAVE STEPS
    // =========================
    const pusherSteps = payload?.steps;

    if (Array.isArray(pusherSteps)) {
      console.log("🔥 New steps received:", pusherSteps);

      setStepData(pusherSteps);

      saveStepsToStorage(pusherSteps);
    }

    // =========================
    // DRIVER LOCATION
    // =========================
    if (payload) {
      if (payload.lat && payload.lng) {
        lat = parseFloat(payload.lat);
        lng = parseFloat(payload.lng);
      } else if (payload.current_lat && payload.current_lon) {
        lat = parseFloat(payload.current_lat);
        lng = parseFloat(payload.current_lon);
      } else if (payload.latitude && payload.longitude) {
        lat = parseFloat(payload.latitude);
        lng = parseFloat(payload.longitude);
      }
    }

    if (lat !== null && lng !== null) {
      const newLocation = {
        lat,
        lng,
      };

      setPrevDriverLocation(driverLocation);
      setDriverLocation(newLocation);

      if (dropoff_lat && dropoff_lon) {
        if (prevDriverLocation) {
          const moveDistance = calculateDistance(
            prevDriverLocation.lat,
            prevDriverLocation.lng,
            lat,
            lng,
          );

          const isOnRoute = isDriverOnRoute(lat, lng, routePath);

          if (!isOnRoute || moveDistance > 0.05) {
            if (routeFetchTimeout.current) {
              clearTimeout(routeFetchTimeout.current);
            }

            routeFetchTimeout.current = setTimeout(() => {
              fetchDriverRoute(lat, lng, dropoff_lat, dropoff_lon);

              setRouteRecalculationCount((prev) => prev + 1);
            }, 500);
          }
        } else {
          fetchDriverRoute(lat, lng, dropoff_lat, dropoff_lon);
        }
      }
    }
  };

  // 🔥 Driver se destination tak ROAD ROUTE with real-time updates
  const fetchDriverRoute = (originLat, originLng, destLat, destLng) => {
    if (!map || !originLat || !originLng || !destLat || !destLng) return;

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin: { lat: originLat, lng: originLng },
        destination: { lat: destLat, lng: destLng },
        travelMode: window.google.maps.TravelMode.DRIVING,
        drivingOptions: {
          departureTime: new Date(),
          trafficModel: window.google.maps.TrafficModel.OPTIMISTIC,
        },
      },
      (response, status) => {
        if (status === "OK") {
          setDriverDirections(response);

          // 🔥 Update route path for route checking
          const route = response.routes[0].overview_path;
          setRoutePath(
            route.map((point) => ({ lat: point.lat(), lng: point.lng() })),
          );
        } else {
          console.warn("⚠️ Failed to fetch route:", status);
        }
      },
    );
  };

  // Pusher setup
  useEffect(() => {
    if (data?.id) {
      const channelName = `driver.${data.id}`;

      pusher.initialize();
      const channel = pusher.subscribeToChannel(channelName);

      if (channel) {
        channel.bind("location.updated", (eventData) => {
          handleDriverLocationUpdate(eventData);
        });

        channel.bind("location-update", (eventData) => {
          handleDriverLocationUpdate(eventData);
        });

        channel.bind_global((eventName, eventData) => {
          if (eventData && (eventData.current_lat || eventData.lat)) {
            handleDriverLocationUpdate(eventData);
          }
        });
      }
    }

    return () => {
      if (routeFetchTimeout.current) {
        clearTimeout(routeFetchTimeout.current);
      }
      pusher.disconnect();
    };
  }, [data?.id]);

  // Data initialization
  useEffect(() => {
    if (data) {
      // ✅ Initial steps
      if (data.steps && data.steps.length > 0) {
        setStepData(data.steps);
        saveStepsToStorage(data.steps);
      }
      if (data.pickup_lat && data.pickup_lon) {
        set_pickup_lat(parseFloat(data.pickup_lat));
        set_pickup_lon(parseFloat(data.pickup_lon));
      } else if (data.steps && data.steps.length > 0) {
        set_pickup_lat(parseFloat(data.steps[0].lat));
        set_pickup_lon(parseFloat(data.steps[0].lon));
      }

      let dropStep = null;
      if (data.steps && data.steps.length > 0) {
        dropStep = data.steps.filter((step) => step.isCompleted == 1);
      }

      if (data.dropoff_lat && data.dropoff_lon) {
        set_dropoff_lat(parseFloat(data.dropoff_lat));
        set_dropoff_lon(parseFloat(data.dropoff_lon));
      } else if (dropStep?.length > 0) {
        set_dropoff_lat(parseFloat(dropStep[dropStep.length - 1].lat));
        set_dropoff_lon(parseFloat(dropStep[dropStep.length - 1].lon));
      } else if (data.steps && data.steps.length > 0) {
        set_dropoff_lat(parseFloat(data.steps[data.steps.length - 1].lat));
        set_dropoff_lon(parseFloat(data.steps[data.steps.length - 1].lon));
      }

      const initialCenter = {
        lat: data.pickup_lat
          ? parseFloat(data.pickup_lat)
          : data.steps && data.steps.length > 0
            ? parseFloat(data.steps[0].lat)
            : 0,
        lng: data.pickup_lon
          ? parseFloat(data.pickup_lon)
          : data.steps && data.steps.length > 0
            ? parseFloat(data.steps[0].lon)
            : 0,
      };
      setMapCenter(initialCenter);
    }
  }, [data]);

  useEffect(() => {
    let dropStep = null;
    if (data.steps && data.steps.length > 0) {
      dropStep = data.steps.filter((step) => step.isCompleted == 1);
      if (dropStep.length > 0 && !data.dropoff_lat && !data.dropoff_lon) {
        setShowEnd(false);
      } else if (dropStep.length > 0 && data.dropoff_lat && data.dropoff_lon) {
        setShowEnd(true);
      }
    }
  }, [data]);

  // 🔥 MAIN ROUTE - Pickup to Dropoff via Waypoints (Static route)
  useEffect(() => {
    if (map && pickup_lat && pickup_lon && dropoff_lat && dropoff_lon) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsServiceRef.current = directionsService;

      // let temparrayforwaypoints = data.steps?.filter(
      //   (item) => item.isCompleted == 1 && item.lat && item.lon,
      // );

      let temparrayforwaypoints = stepsData?.filter(
        (item) => item.isCompleted == 1 && item.lat && item.lon,
      );

      const waypointsData = temparrayforwaypoints?.map((item) => ({
        location: {
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon),
        },
      }));

      directionsService.route(
        {
          origin: { lat: pickup_lat, lng: pickup_lon },
          destination: { lat: dropoff_lat, lng: dropoff_lon },
          travelMode: window.google.maps.TravelMode.DRIVING,
          waypoints: waypointsData,
          optimizeWaypoints: false,
        },
        (response, status) => {
          if (status === "OK") {
            setDirections(response);

            const route = response.routes[0];

            // ==========================================
            // 🚗 TOTAL TRAVEL TIME
            // ==========================================

            const totalDuration = route.legs?.reduce(
              (total, leg) => total + (leg.duration?.value || 0),
              0,
            );

            // ==========================================
            // 📏 TOTAL ROAD DISTANCE
            // ==========================================

            const totalDistance = route.legs?.reduce(
              (total, leg) => total + (leg.distance?.value || 0),
              0,
            );

            // Seconds → Minutes
            const durationMinutes = Math.ceil(totalDuration / 60);

            // Meters → KM
            const distanceKm = (totalDistance / 1000).toFixed(1);

            // Save in state
            setTravelTime(`${durationMinutes} min`);
            setTravelDistance(`${distanceKm} km`);

            console.log("🚗 Total Travel Time:", durationMinutes, "minutes");
            console.log("📏 Total Travel Distance:", distanceKm, "km");

            // ==========================================
            // 🗺️ ROUTE PATH
            // ==========================================

            const routePathData = route.overview_path;

            setRoutePath(
              routePathData.map((point) => ({
                lat: point.lat(),
                lng: point.lng(),
              })),
            );

            // ==========================================
            // 📍 WAYPOINTS
            // ==========================================

            const wp = route.legs;

            setWaypoints(
              wp.map((point) => ({
                lat: point.start_location.lat(),
                lng: point.start_location.lng(),
              })),
            );
          } else {
            console.warn("⚠️ Failed to fetch main route:", status);
          }
        },
      );
    }
  }, [map, pickup_lat, pickup_lon, dropoff_lat, dropoff_lon, stepsData]);

  const onLoad = (mapInstance) => {
    mapInstance.setCenter(mapCenter);
    mapInstance.setZoom(17);
    setMap(mapInstance);
    mapRef.current = mapInstance;
  };

  const onUnmount = () => {
    setMap(null);
    mapRef.current = null;
  };

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={mapCenter}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{ maxZoom: 20 }}
    >
      {routePath.length > 0 && (
        <>
          {/* Pickup Marker */}
          <Marker
            position={{ lat: pickup_lat, lng: pickup_lon }}
            icon={{
              url: start,
              scaledSize: new window.google.maps.Size(80, 100),
            }}
          />

          {/* 🔥 DRIVER MARKER */}
          {driverLocation && (
            <Marker
              position={driverLocation}
              icon={{
                url: steps_icon,
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(25, 25),
              }}
            />
          )}

          {/* 🔥 MAIN ROUTE - Static route */}
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                polylineOptions: {
                  strokeColor: "#f90001",
                  strokeOpacity: 1.0,
                  strokeWeight: 5,
                },
                suppressMarkers: true,
                preserveViewport: true,
              }}
            />
          )}

          {/* Waypoints Overlay */}
          {/* {data.steps && data.steps.some((step) => step.isCompleted == 1) && ( */}
          {stepsData && stepsData.some((step) => step.isCompleted == 1) && (
            <>
              {waypoints.map((waypoint, index) => (
                <React.Fragment key={index}>
                  {data.steps[index - 1]?.name && (
                    <Marker
                      position={{ lat: waypoint.lat, lng: waypoint.lng }}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        fillColor: "#ffffff",
                        fillOpacity: 1,
                        strokeColor: "#f90001",
                        strokeOpacity: 1,
                        strokeWeight: 4,
                        scale: 6,
                      }}
                    />
                  )}
                  {data.steps[index - 1]?.name && (
                    <OverlayView
                      position={{ lat: waypoint.lat, lng: waypoint.lng }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div
                        style={{
                          height: 45,
                          width: 140,
                          backgroundColor: "#f90001",
                          borderRadius: "15px",
                          color: "white",
                          textAlign: "center",
                          justifyContent: "center",
                          alignItems: "center",
                          display: "flex",
                          flexDirection: "column",
                          marginTop: -24,
                          marginLeft: 15,
                          paddingBottom: 0,
                          paddingTop: 0,
                        }}
                      >
                        <p style={{ padding: 0, margin: 0 }}>
                          {stepsData[index - 1]?.name}
                        </p>
                        <p style={{ padding: 0, margin: 0 }}>
                          {moment
                            .utc(stepsData[index - 1]?.updated_at)
                            .tz(
                              Intl.DateTimeFormat().resolvedOptions().timeZone,
                            )
                            .format("DD-MMM-YYYY h:mm A")}
                        </p>
                      </div>
                    </OverlayView>
                  )}
                </React.Fragment>
              ))}
            </>
          )}

          {/* Dropoff Marker */}
          {showEnd && (
            <Marker
              position={{ lat: dropoff_lat, lng: dropoff_lon }}
              icon={{
                url: end,
                scaledSize: new window.google.maps.Size(80, 100),
              }}
            />
          )}
        </>
      )}

      {/* Status Display */}
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          backgroundColor: "rgba(255,255,255,0.95)",
          padding: "10px 14px",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          fontSize: "12px",
          zIndex: 1000,
          minWidth: "160px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: driverLocation ? "#4CAF50" : "#f44336",
              animation: driverLocation
                ? "pulse 1.5s ease-in-out infinite"
                : "none",
            }}
          />
          <span style={{ fontWeight: "bold" }}>
            {driverLocation ? "Live" : "⏳ Waiting..."}
          </span>
        </div>
        {driverLocation && (
          <div style={{ fontSize: "10px", color: "#666", marginTop: "4px" }}>
            📍 {driverLocation.lat.toFixed(6)}, {driverLocation.lng.toFixed(6)}
          </div>
        )}
        {routeRecalculationCount > 0 && (
          <div style={{ fontSize: "10px", color: "#FF6B35", marginTop: "2px" }}>
            🔄 Route updated: {routeRecalculationCount}x
          </div>
        )}
        {travelTime && travelDistance && (
          <div
            style={{
              fontSize: "12px",
              color: "#303030",
              marginTop: "6px",
              paddingTop: "6px",
              borderTop: "1px solid #eee",
              fontWeight: "600",
            }}
          >
            🚗 {travelDistance} • ⏱️ {travelTime}
          </div>
        )}
      </div>

      {/* Map icon legend */}
      <div
        aria-label="Map icon legend"
        style={{
          position: "absolute",
          bottom: 10,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "clamp(12px, 3vw, 26px)",
          width: "min(92%, 420px)",
          boxSizing: "border-box",
          padding: "8px 13px",
          backgroundColor: "rgba(255, 255, 255, 0.96)",
          borderRadius: "16px",
          boxShadow: "0 3px 12px rgba(0, 0, 0, 0.18)",
          zIndex: 1000,
        }}
      >
        {[
          { icon: start, label: "Start", size: 32 },
          { icon: end, label: "End", size: 32 },
          { icon: null, label: "Steps", size: 32 },
          { icon: steps_icon, label: "Driver", size: 32 },
        ].map(({ icon, label, size }) => (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "#303030",
              fontSize: "clamp(11px, 2.5vw, 14px)",
              whiteSpace: "nowrap",
            }}
          >
            {icon ? (
              <img
                src={icon}
                alt={`${label} icon`}
                style={{ width: size, height: size, objectFit: "contain" }}
              />
            ) : (
              <span
                aria-label="Steps icon"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  border: "4px solid #f90001",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                }}
              />
            )}
            <span>{label}</span>
          </div>
        ))}
      </div>

      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(0.8); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
    </GoogleMap>
  ) : (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <p>Loading map...</p>
    </div>
  );
}

export default React.memo(Maps);
